/**
 * Visual CMS — theme-safe color palette.
 *
 * Admins choose backgrounds/text from this curated list to keep section styles
 * aligned with the v2 design system. Free-form hex/CSS strings are still
 * accepted by the DB (text columns), but the picker UX nudges admins toward
 * these tokens. Renderers apply the value as an inline `background-color` /
 * `color`; resolved colors below are CSS values, not Tailwind classes, so they
 * work consistently in admin preview + public render without depending on the
 * Tailwind safelist.
 */

export interface ThemeColor {
  /** Stored value (the `background_color` / `text_color` column). */
  value: string;
  /** Display name in the picker. */
  label: string;
  /** Hex/CSS color used to render the swatch and the actual section style. */
  cssColor: string;
  /** Hint of contrasting on-color for readable swatch labels. */
  onColor: "light" | "dark";
}

/** Background palette — neutral surfaces + brand accents from the v2 system. */
export const VISUAL_CMS_BACKGROUND_PALETTE: ThemeColor[] = [
  { value: "white", label: "White", cssColor: "#ffffff", onColor: "dark" },
  { value: "neutral-50", label: "Neutral 50", cssColor: "#fafafa", onColor: "dark" },
  { value: "neutral-100", label: "Neutral 100", cssColor: "#f5f5f5", onColor: "dark" },
  { value: "neutral-900", label: "Neutral 900", cssColor: "#171717", onColor: "light" },
  { value: "green-50", label: "Brand 50", cssColor: "#f0fdf4", onColor: "dark" },
  { value: "green-100", label: "Brand 100", cssColor: "#dcfce7", onColor: "dark" },
  { value: "green-600", label: "Brand 600", cssColor: "#16a34a", onColor: "light" },
  { value: "amber-50", label: "Amber 50", cssColor: "#fffbeb", onColor: "dark" },
  { value: "slate-900", label: "Slate 900", cssColor: "#0f172a", onColor: "light" },
];

/** Text palette — sensible foreground colors. */
export const VISUAL_CMS_TEXT_PALETTE: ThemeColor[] = [
  { value: "neutral-900", label: "Neutral 900", cssColor: "#171717", onColor: "light" },
  { value: "neutral-700", label: "Neutral 700", cssColor: "#404040", onColor: "light" },
  { value: "neutral-500", label: "Neutral 500", cssColor: "#737373", onColor: "light" },
  { value: "white", label: "White", cssColor: "#ffffff", onColor: "dark" },
  { value: "green-700", label: "Brand 700", cssColor: "#15803d", onColor: "light" },
  { value: "amber-700", label: "Amber 700", cssColor: "#b45309", onColor: "light" },
  { value: "red-700", label: "Red 700", cssColor: "#b91c1c", onColor: "light" },
];

const ALL_PALETTE = [...VISUAL_CMS_BACKGROUND_PALETTE, ...VISUAL_CMS_TEXT_PALETTE];

/**
 * Resolve a stored color value to a CSS color. Accepts:
 *   - a palette `value` token (e.g. "neutral-900")
 *   - an explicit hex string ("#abc", "#aabbcc")
 *   - rgb()/rgba() strings
 *
 * Anything else (or empty) returns null so renderers fall back to the design
 * system default for that slot.
 */
export function resolveThemeColor(value: string | null | undefined): string | null {
  if (!value) return null;
  const v = value.trim();
  if (!v) return null;
  const palette = ALL_PALETTE.find((c) => c.value === v);
  if (palette) return palette.cssColor;
  if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v)) return v;
  if (/^rgb(a)?\(/i.test(v)) return v;
  return null;
}
